import { useMemo } from 'react';
import { css } from '@emotion/react';
import { InsuranceVerificationDetailsResponse } from '@weave/schema-gen-ts/dist/schemas/insurance-verification/v1/insurance_verification_adapter.pb';
import { InsuranceVerificationDetailsResponse as InsuranceVerificationDetailsResponseV2 } from '@weave/schema-gen-ts/dist/schemas/insurance-verification/v2/insurance_verification_adapter.pb';
import { formatDate } from '@frontend/date';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { SpinningLoader, styles, Text, Chip, Truncated } from '@frontend/design-system';
import { AlertIcon } from '@frontend/design-system';
import { useGetVerifiedInsuranceDetailsList } from '../hooks/use-get-verified-insurance-details-list';
import { PayerListDetails, UndefinedOrNull } from '../utils/types';
import { AlternativePayerIds } from './alternative-payer-ids';
import { DetailText } from './detail-text';

interface Props {
  insuranceVerificationAPIResData: UndefinedOrNull<InsuranceVerificationDetailsResponse>;
  locationId: string;
  pmsPayerId?: string;
  isIntegratedOffice?: boolean;
  payersList: UndefinedOrNull<PayerListDetails[]>;
  isEnhancedInsuranceVerificationEnabled: boolean;
}

export const VerifiedInsuranceDetailsList = ({
  insuranceVerificationAPIResData,
  locationId,
  pmsPayerId,
  isIntegratedOffice = false,
  payersList,
  isEnhancedInsuranceVerificationEnabled,
}: Props) => {
  const { t } = useTranslation('insuranceDetails');
  const {
    isLoading,
    effectiveDate,
    endDate,
    groupNumber,
    groupName,
    payerId,
    payerName,
    memberId,
    mismatchedMemberId,
  } = useGetVerifiedInsuranceDetailsList({
    insuranceDetails: insuranceVerificationAPIResData as UndefinedOrNull<InsuranceVerificationDetailsResponseV2>,
    locationId,
  });

  const hasPMSPayerID = isIntegratedOffice && !!pmsPayerId;

  const alternativePayerIds = useMemo(() => {
    if (!isEnhancedInsuranceVerificationEnabled) return [];

    const payerIds = payersList?.find(
      (payer) => payer.id === payerId || payer.name?.toLowerCase() === payerName?.toLowerCase()
    )?.alternatePayerIds;

    return payerIds || [];
  }, [isEnhancedInsuranceVerificationEnabled, payerId, payerName, payersList]);

  const shouldRenderAlternativePayerIds = !hasPMSPayerID && !!alternativePayerIds.length;

  return (
    <>
      {isLoading ? (
        <div css={styles.flexCenter}>
          <SpinningLoader size='medium' />
        </div>
      ) : (
        <dl css={listStyle}>
          <div>
            <Text as='dt'>{t('Payer Name')}</Text>
            <DetailText value={payerName} />
          </div>
          <div>
            <Text as='dt'>{t('Payer ID')}</Text>
            <DetailText value={payerId} required />
          </div>
          {isIntegratedOffice && !shouldRenderAlternativePayerIds && (
            <div>
              <Text as='dt'>{t('PMS Payer ID')}</Text>
              <DetailText value={pmsPayerId || ''} />
            </div>
          )}
          {shouldRenderAlternativePayerIds && (
            <div>
              <Text as='dt'>{t('Alternate Payer IDs')}</Text>
              <AlternativePayerIds payerIds={alternativePayerIds} />
            </div>
          )}
          <div>
            <Text as='dt'>{t('Group Name')}</Text>
            <DetailText value={groupName} />
          </div>
          <div>
            <Text as='dt'>{t('Group Number')}</Text>
            <DetailText value={groupNumber} />
          </div>
          <div>
            <Text as='dt'>{t('Member ID')}</Text>
            {mismatchedMemberId ? (
              <div css={mismatchedMemberIdStyle}>
                <div css={memberIdWithChipStyle}>
                  <Truncated as='dd'>{mismatchedMemberId}</Truncated>

                  <Chip
                    css={{ backgroundColor: `${theme.colors.warning30}40`, color: theme.colorsV2.yellow70() }}
                    size='tiny'
                    leftElement={<AlertIcon size={12} css={{ color: theme.colorsV2.yellow70() }} />}
                  >
                    {t('Differs')}
                  </Chip>
                </div>
                <Text size='medium' css={requestedMemberIdStyle}>
                  {t('Requested')}: {memberId}
                </Text>
              </div>
            ) : (
              <DetailText value={memberId} />
            )}
          </div>
          <div>
            <Text as='dt'>{t('Effective Date')}</Text>
            <DetailText value={effectiveDate ? formatDate(effectiveDate, 'MMMM D, YYYY') : '-'} />
          </div>

          <div>
            <Text as='dt'>{t('End Date')}</Text>
            <DetailText value={endDate ? formatDate(endDate, 'MMMM D, YYYY') : '-'} />
          </div>
        </dl>
      )}
    </>
  );
};

const listStyle = css`
  margin: 0;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: ${theme.spacing(1)};
  grid-auto-flow: dense;
  > div {
    display: flex;
    flex-direction: column;

    dt {
      margin-right: ${theme.spacing(4)};
      font-size: ${theme.fontSize(12)};
      margin-bottom: ${theme.spacing(0.5)};
      color: ${theme.font.colors.subdued};
    }
  }
`;

const mismatchedMemberIdStyle = css`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing(0.5)};
`;

const memberIdWithChipStyle = css`
  display: flex;
  align-items: center;
  gap: ${theme.spacing(0.75)};

  dd {
    font-size: ${theme.fontSize(14)};
    font-weight: ${theme.font.weight.bold};
    white-space: nowrap;
  }
`;

const requestedMemberIdStyle = css`
  color: ${theme.colorsV2.yellow70()};
`;
